import { Avatar, Box, Card, CardActions, CardContent, CardMedia, Grid, StepConnector, Typography, useTheme } from '@mui/material';
import { Card as TCard } from '@/types/generated';
import { getCardStyles, getCardContentStyles, getCardActionStyles, pdfLinkStyles, getAvatarStyles, getStepperCardBoxStyles, getCardMediaStyles } from './CardItem.styles';
import AspectRatioPaddingBox from '@shared-components/components/AspectRatioPaddingBox';
import ContentfulButton from '@shared-components/components/ContentfulButton';
import ImageAsset from '@shared-components/components/ImageAsset';
import { IImage } from '@shared-components/components/ImageAsset/ImageAsset';
import ImageWrapper from '@shared-components/components/ImageWrapper';
import { getCardButtonAriaLabel } from '@shared-components/components/MultiCard/MultiCard.helpers';
import ResourceCardTile from '@shared-components/components/MultiCard/ResourceCardTile';
import RichText, { RichTextContent } from '@shared-components/components/RichText/RichText';
import TAGLink from '@shared-components/components/TAGLink';
import TAGSvgIcon from '@shared-components/components/TAGSvgIcon';
import TermsAndConditions from '@shared-components/components/TermsAndConditions';
import { HeadingTag } from '@shared-components/types';
import { textToJumpLink } from '@utils';
import getHeadingVariant from '@utils/resolveContentfulHeadingVariant';
interface ICardItem {
  card: TCard;
  isSmallScreen: boolean;
  isDarkBackground?: boolean;
  displayAsStepper?: boolean;
  showLeftConnector?: boolean;
  showRightConnector?: boolean;
  enableResourceCardMode?: boolean;
  useMediaRoundedCorners?: boolean;
}
function resolveCardTextAlign(displayAsStepper: boolean, isSmallScreen: boolean, cardTextAlignment?: string | null): 'left' | 'center' {
  if (displayAsStepper && isSmallScreen) return 'left';
  return cardTextAlignment === 'center' ? 'center' : 'left';
}
function resolveCardTextColor(isDarkBackground?: boolean, backgroundColor?: string | null, variant: 'primary' | 'secondary' = 'secondary'): string {
  if (isDarkBackground && backgroundColor === 'transparent') {
    return 'text.light';
  }
  return variant === 'primary' ? 'text.primary' : 'text.secondary';
}

/**
 * Safely render a value as a string ReactNode
 * Filters out objects and other invalid ReactNode types
 */
function safeTextNode(value: unknown): string | null {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (value === null || value === undefined) {
    return null;
  }
  // Log invalid values for debugging
  console.warn('CardItem: Attempted to render invalid text value:', value);
  return null;
}
type StepperAvatarContentProps = {
  card: TCard;
  iconColor: string;
};
function StepperAvatarContent({
  card,
  iconColor
}: Readonly<StepperAvatarContentProps>) {
  if (card.imageAsset) {
    return <ImageAsset desktopImage={card.imageAsset.desktopImage as IImage} mobileImage={card.imageAsset.mobileImage as IImage} isBackgroundImage={true} dataTestId={`multicard_as_stepper_avatar_image_asset_${card.sys.id}`} />;
  }
  if (card.cardIcon) {
    return <TAGSvgIcon width="60" height="60" icon={card.cardIcon} htmlColor={iconColor} dataTestId={`multicard_as_stepper_avatar_icon_${card.sys.id}`} />;
  }
  return null;
}
type StepperVisualProps = {
  card: TCard;
  isSmallScreen: boolean;
  showLeftConnector?: boolean;
  showRightConnector?: boolean;
  iconColor: string;
};
function StepperVisual({
  card,
  isSmallScreen,
  showLeftConnector,
  showRightConnector,
  iconColor
}: Readonly<StepperVisualProps>) {
  const theme = useTheme();
  const hasImageOrIcon = Boolean(card.cardIcon || card.imageAsset);
  return <Box sx={getStepperCardBoxStyles(isSmallScreen)} data-testid={`multicard_as_stepper_step_item_${card.sys.id}`} data-sentry-element="Box" data-sentry-component="StepperVisual" data-sentry-source-file="CardItem.tsx">
			{!isSmallScreen && <StepConnector sx={{
      visibility: showLeftConnector ? 'visible' : 'hidden'
    }} />}
			<Avatar sx={getAvatarStyles(theme, hasImageOrIcon)} data-sentry-element="Avatar" data-sentry-source-file="CardItem.tsx">
				<StepperAvatarContent card={card} iconColor={iconColor} data-sentry-element="StepperAvatarContent" data-sentry-source-file="CardItem.tsx" />
			</Avatar>
			<StepConnector sx={{
      visibility: showRightConnector ? 'visible' : 'hidden'
    }} data-sentry-element="StepConnector" data-sentry-source-file="CardItem.tsx" />
		</Box>;
}
type CardImageSectionProps = {
  card: TCard;
  useMediaRoundedCorners: boolean;
};
function CardImageSection({
  card,
  useMediaRoundedCorners
}: Readonly<CardImageSectionProps>) {
  if (card.imageAsset) {
    return <AspectRatioPaddingBox aspectRatio="5:4" contentfulAspectRatio={card.imageRatio} paddingBoxSx={{
      ...getCardMediaStyles(useMediaRoundedCorners),
      height: 'auto'
    }}>
				<CardMedia component={card.buttonUrl ? 'a' : 'div'} href={card.buttonUrl ?? undefined} data-testid={`group_card_image_asset_link_${card.sys.id}`} sx={{
        ...getCardMediaStyles(useMediaRoundedCorners),
        overflow: 'hidden'
      }}>
					<ImageAsset desktopImage={card.imageAsset.desktopImage as IImage} mobileImage={card.imageAsset.mobileImage as IImage} isBackgroundImage={false} dataTestId={`img_asset_card_item_background_${card.sys.id}`} />
				</CardMedia>
			</AspectRatioPaddingBox>;
  }
  if (!card.image?.url) {
    return null;
  }
  return <CardMedia component={card.imageUrl ? 'a' : 'div'} {...card.imageUrl ? {
    href: card.imageUrl
  } : {}} data-testid={`group_card_image_link_${card.sys.id}`} sx={{
    ...getCardMediaStyles(useMediaRoundedCorners),
    overflow: 'hidden'
  }} data-sentry-element="CardMedia" data-sentry-component="CardImageSection" data-sentry-source-file="CardItem.tsx">
			<AspectRatioPaddingBox aspectRatio="5:4" contentfulAspectRatio={card.imageRatio} paddingBoxSx={getCardMediaStyles(useMediaRoundedCorners)} data-sentry-element="AspectRatioPaddingBox" data-sentry-source-file="CardItem.tsx">
				<ImageWrapper maxWidth="100%" minWidth="100%" background-size="cover" background-repeat="no-repeat" background-position="center center" object-fit="cover" src={card.imageUrl ?? card.image?.url} alt={card.image?.description ?? ''} data-sentry-element="ImageWrapper" data-sentry-source-file="CardItem.tsx" />
			</AspectRatioPaddingBox>
		</CardMedia>;
}
type CardButtonActionsProps = {
  cardButtons: NonNullable<TCard['buttonsCollection']>['items'] | undefined;
  card: TCard;
  isSmallScreen: boolean;
  displayAsStepper: boolean;
};
function CardButtonActions({
  cardButtons,
  card,
  isSmallScreen,
  displayAsStepper
}: Readonly<CardButtonActionsProps>) {
  if (!cardButtons || cardButtons.length === 0) return null;
  return <>
			{cardButtons.map(button => button ? <CardActions key={button.sys?.id} sx={getCardActionStyles({
      isSmallScreen,
      cardActionAlignment: card.cardTextAlignment,
      displayAsStepper
    })}>
						<ContentfulButton {...button} defaultVariant="secondaryDefault" dataTestId={`group_card_button_${button.name ?? ''}`} ariaLabel={getCardButtonAriaLabel(button.label, card.title)} />
					</CardActions> : null)}
		</>;
}
type DeprecatedButtonActionProps = {
  card: TCard;
  isSmallScreen: boolean;
  displayAsStepper: boolean;
};
function DeprecatedButtonAction({
  card,
  isSmallScreen,
  displayAsStepper
}: Readonly<DeprecatedButtonActionProps>) {
  if (!card.buttonUrl) return null;
  return <CardActions sx={getCardActionStyles({
    isSmallScreen,
    cardActionAlignment: card.cardTextAlignment,
    displayAsStepper
  })} data-sentry-element="CardActions" data-sentry-component="DeprecatedButtonAction" data-sentry-source-file="CardItem.tsx">
			<ContentfulButton _id={card.sys.id} sys={card.sys} buttonLink={card.buttonUrl} label={card.buttonText} defaultVariant="secondaryDefault" variant={card.buttonVariant} icon={card.buttonIcon} iconPosition={card.buttonIconPosition} iconSize={card.buttonIconSize} size={card.buttonSize ?? 'medium'} dataTestId="button_card_action" contentfulMetadata={card.contentfulMetadata} analyticsContext={card.analyticsContext} ariaLabel={getCardButtonAriaLabel(card.buttonText, card.title)} data-sentry-element="ContentfulButton" data-sentry-source-file="CardItem.tsx" />
		</CardActions>;
}
type CardTermsAndConditionsProps = {
  card: TCard;
  isSmallScreen: boolean;
};
function CardTermsAndConditions({
  card,
  isSmallScreen
}: Readonly<CardTermsAndConditionsProps>) {
  if (!card.tc) return null;
  return <Box position="relative" paddingX={isSmallScreen ? '1rem' : 0} data-testid={`group_card_terms_and_conditions_wrapper_${card.sys.id}`} data-sentry-element="Box" data-sentry-component="CardTermsAndConditions" data-sentry-source-file="CardItem.tsx">
			<TermsAndConditions {...card.tc} textSx={{
      textAlign: card.cardTextAlignment ?? 'left'
    }} data-sentry-element="TermsAndConditions" data-sentry-source-file="CardItem.tsx" />
		</Box>;
}
export default function CardItem({
  card,
  isDarkBackground,
  isSmallScreen,
  displayAsStepper = false,
  showLeftConnector,
  showRightConnector,
  enableResourceCardMode = false,
  useMediaRoundedCorners = false
}: Readonly<ICardItem>) {
  const isDarkWrapperBackground = isDarkBackground ?? false;
  const cardButtons = card?.buttonsCollection?.items;
  const iconColor = isDarkWrapperBackground ? 'neutrals.white' : 'tertiary.main';
  if (card.imageAsset && enableResourceCardMode) {
    return <ResourceCardTile card={card} isSmallScreen={isSmallScreen} useMediaRoundedCorners={useMediaRoundedCorners} />;
  }
  return <Card data-testid={`group_card_${card.sys.id}`} id={textToJumpLink(card.title)} sx={getCardStyles({
    backgroundColor: card.backgroundColor,
    isDarkWrapperBackground,
    displayAsStepper,
    isSmallScreen
  })} data-sentry-element="Card" data-sentry-component="CardItem" data-sentry-source-file="CardItem.tsx">
			{displayAsStepper && <StepperVisual card={card} isSmallScreen={isSmallScreen} showLeftConnector={showLeftConnector} showRightConnector={showRightConnector} iconColor={iconColor} />}

			{!displayAsStepper && <CardImageSection card={card} useMediaRoundedCorners={useMediaRoundedCorners} />}

			<CardContent sx={getCardContentStyles(isSmallScreen, displayAsStepper, card.cardTextAlignment)} data-sentry-element="CardContent" data-sentry-source-file="CardItem.tsx">
				{card.cardIcon && !displayAsStepper && <Grid item sx={{
        display: 'flex',
        justifyContent: card.cardIconAlignment,
        width: '100%'
      }}>
						<TAGSvgIcon size={64} htmlColor={iconColor} icon={card.cardIcon} dataTestId={`card_icon_${card.cardIcon}`} />
					</Grid>}
				{safeTextNode(card.eyebrow) && <Typography lineHeight="1rem" fontSize="0.875rem" color={resolveCardTextColor(isDarkBackground, card.backgroundColor)} variant={isSmallScreen ? 'bodySmallBook' : 'bodyMediumBook'} data-testid={`text_card_eyebrow_${card.sys.id}`}>
						{safeTextNode(card.eyebrow)}
					</Typography>}
				{safeTextNode(card.title) && <Typography variant={getHeadingVariant(card.titleHtag as HeadingTag)} color={resolveCardTextColor(isDarkBackground, card.backgroundColor, 'primary')} data-testid={`text_card_title_${card.sys.id}`}>
						{safeTextNode(card.title)}
					</Typography>}
				{safeTextNode(card.description) && <Typography color={resolveCardTextColor(isDarkBackground, card.backgroundColor)} variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'} data-testid={`text_card_description_${card.sys.id}`} textAlign={resolveCardTextAlign(displayAsStepper, isSmallScreen, card.cardTextAlignment)}>
						{safeTextNode(card.description)}
					</Typography>}
				{card.richTextDescription && <RichText content={card.richTextDescription.json as RichTextContent} links={card.richTextDescription.links} docProps={{
        textAlign: resolveCardTextAlign(displayAsStepper, isSmallScreen, card.cardTextAlignment)
      }} />}
			</CardContent>

			<CardButtonActions cardButtons={cardButtons} card={card} isSmallScreen={isSmallScreen} displayAsStepper={displayAsStepper} data-sentry-element="CardButtonActions" data-sentry-source-file="CardItem.tsx" />

			<DeprecatedButtonAction card={card} isSmallScreen={isSmallScreen} displayAsStepper={displayAsStepper} data-sentry-element="DeprecatedButtonAction" data-sentry-source-file="CardItem.tsx" />

			{card.pdfFileLink?.url && <TAGLink href={card.pdfFileLink.url} openInNewTab linkSx={pdfLinkStyles}>
					<Typography color="text.interactive" variant="bodyMediumBold" title="Download">
						{card.pdfDownloadTitle ?? 'Download'}
					</Typography>
				</TAGLink>}

			<CardTermsAndConditions card={card} isSmallScreen={isSmallScreen} data-sentry-element="CardTermsAndConditions" data-sentry-source-file="CardItem.tsx" />
		</Card>;
}